<template>
  <div class="relative flex w-full h-screen overflow-hidden">
    <div v-if="isLoading" class="absolute top-0 bottom-0 left-0 right-0 z-50 flex flex-col items-center justify-start w-full h-screen overflow-hidden bg-gray-900 bg-opacity-30">
      <div class="w-10 h-10 mt-20 ease-linear border-4 border-t-4 border-gray-200 border-top-rounded-full loader"></div>
    </div>

    <!-- Mobile Filter Overlay -->
    <div
      v-show="mobileFiltersOpen"
      class="fixed inset-0 z-40 md:hidden"
      @click="toggleMobileFilters"
    >
      <div class="absolute inset-0 bg-gray-600 bg-opacity-75"></div>
    </div>

    <!-- Facet Filters - Desktop: Static, Mobile: Slide-out -->
    <div
      :class="[
        'md:flex md:flex-shrink-0',
        'fixed md:relative inset-y-0 left-0 z-50 md:z-0 w-4/5 md:w-auto md:h-full',
        'transform transition-transform duration-300 ease-in-out md:translate-x-0',
        mobileFiltersOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
      name="facet-filters"
    >
      <div class="flex flex-col h-full bg-gray-900 md:w-64 lg:w-72 xl:w-96">
        <div class="flex items-center justify-between p-4 md:hidden">
          <h2 class="text-lg font-medium text-gray-200">Filter Results</h2>
          <button
            type="button"
            class="inline-flex items-center justify-center p-2 -mr-2 text-gray-400 hover:text-gray-200 focus:outline-none"
            @click="toggleMobileFilters"
          >
            <span class="sr-only">Close menu</span>
            <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="flex flex-col w-full h-full overflow-y-auto">
          <div class="w-full divide-y divide-gray-700">
              <p
                class="hidden w-full p-4 font-semibold leading-4 tracking-wider uppercase bg-gray-800 md:block text-cyan-100 text-md xl:px-6"
              >
                Filter Results
              </p>

              <div class="flex items-end justify-between p-4 space-x-2 xl:py-6 xl:px-8">
                <div v-tooltip="!isSubscribed && 'Upgrade to unlock more search results'">
                  <p class="text-xs font-semibold leading-4 text-gray-200">
                    Per Page
                  </p>

                  <select
                    name="page_size"
                    id="page_size"
                    class="flex py-1 pl-2 pr-8 mt-1 text-xs font-semibold leading-5 bg-gray-200 border-gray-400 rounded focus:border-blue-600 focus:outline-none focus:ring-blue-600"
                    @change="updatePageSize"
                    :disabled="!isSubscribed"
                  >
                    <option value=10 :selected="serverParams.pageSize == 10">10</option>
                    <option value=20 :selected="serverParams.pageSize == 20" >20</option>
                    <option value=50 :selected="serverParams.pageSize == 50">50</option>
                    <option value=100 :selected="serverParams.pageSize == 100">100</option>
                  </select>
                </div>
                <div class="ml-2"
                  v-if="modelUsesSavedSearches"
                >
                  <a :href="this.route('saved_searches.index', {'model': modelName})" class="py-1 text-xs font-semibold rounded tw-simple-button">
                    My Saved Searches
                  </a>
                </div>
              </div>
              <facet-filter
                v-for="(value, key) in this.facetFilters"
                :key=key
                :refs="key"
                :facetFields="value"
                :field-name="getMetaData(key).field"
                :facetType="getMetaData(key).type"
                :label="getMetaData(key).label"
                :selectedFields="serverParams.filters"
                :infoButton="getMetaData(key).info"
                @set-filters="setFilters"
              >
              </facet-filter>
            </div>
        </div>
      </div>
    </div>
    <tw-modal
      ref="modal"
    >
      <template v-slot:content>
        <div class="w-full px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
          <div class="w-full sm:flex sm:items-start">
            <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-blue-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
              <!-- Heroicon name: outline/exclamation -->
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                Save this search
              </h3>
              <div class="mt-2">
                <ul>
                  <li
                    class="text-sm text-red-600"
                    :class="{'hidden': !errorMessages.length}"
                    v-for="(error, index) in errorMessages"
                    :key=index
                  >
                    {{ error }}
                  </li>
                </ul>
                <p class="text-sm text-gray-500">Name</p>
                <input v-if="isSavedSearch" type="text" name="name" :value="savedSearchInfo.name" id="save-search-name" class="block w-full min-w-0 border-gray-300 rounded-md sm:text-sm focus:ring-blue-500 focus:border-blue-500">
                <input v-else type="text" name="name" id="save-search-name" class="block w-full min-w-0 border-gray-300 rounded-md sm:text-sm focus:ring-blue-500 focus:border-blue-500">
                <div class="mt-4">
                  <label for="save-search-description" class="block text-sm text-gray-500">Description</label>
                  <div class="mt-1">
                    <textarea v-if="isSavedSearch" rows="4" name="description" :value="savedSearchInfo.description" id="save-search-description" class="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"></textarea>
                    <textarea v-else rows="4" name="description" id="save-search-description" class="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!isSavedSearch" class="flex justify-end px-4 py-3 space-x-2 sm:space-x-4 bg-gray-50 sm:px-6">
          <button type="button" @click="storeSavedSearch" class="tw-button-green">Save</button>
          <button type="button" @click="closeSaveSearchModal" class="tw-button-gray">Cancel</button>
        </div>
        <div v-else class="flex justify-end px-4 py-3 space-x-2 sm:space-x-4 bg-gray-50 sm:px-6">
          <button type="button" @click="closeSaveSearchModal" class="tw-button-gray">Cancel</button>
          <button type="button" @click="updateSavedSearch" class="tw-button-green">Update</button>
          <span class="text-xs">or</span>
          <button type="button" @click="storeSavedSearch" class="tw-button-green">Save New</button>
        </div>
      </template>
    </tw-modal>
    <div class="flex flex-col flex-1 w-0 h-screen overflow-hidden">
      <main
        class="relative z-0 flex-1 w-full py-4 overflow-y-auto md:py-6 focus:outline-none"
        tabindex="0"
      >
        <div class="pb-16 mx-auto sm:px-4 max-w-7xl md:px-8">
          <div class="flex items-start justify-between px-4 sm:px-0">
            <div>
              <h1
                class="text-xl font-semibold leading-7 text-gray-700 sm:text-2xl sm:leading-9 sm:truncate"
              >
                {{ pageTitle }}
              </h1>
              <p
                v-if="subTitle && subTitle.length"
                class="text-xs font-medium text-gray-500"
              >
                {{ subTitle }}
              </p>
            </div>
            <div class="flex space-x-4">
              <a
                v-if="includeMap"
                id="mapButton"
                class="tw-simple-toggle-button"
                @click.prevent="toggleMapView"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 20 26"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  ></path>
                </svg>
                <span id="mapButtonText" class="hidden md:flex">
                  Show Map View
                </span>
              </a>
              <button
                @click="toggleMobileFilters"
                class="tw-simple-toggle-button md:hidden"
              >
                <svg v-if=!mobileFiltersOpen xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0" />
                </svg>

                <svg
                  v-else
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>

              </button>
              <a
                v-if="canCreate"
                class="tw-button-add-model"
                :href="createRoute"
              >
                Add New
              </a>
            </div>
          </div>

          <div class="flex items-center justify-between px-4 mt-4 sm:px-0">
            <div class="flex-grow">
              <input v-debounce:200ms="updateSearch" type="search" id="elastic-search-input" @search="searchClear" :placeholder="this.placeHolder" class="block w-full p-2 text-xs border-gray-300 rounded-md shadow focus:border-blue-500 focus:ring-0 md:p-4 sm:p-3 sm:leading-5 sm:text-sm"/>
            </div>
          </div>
          <div
            v-if="!onlyExcludedFields() || serverParams.filters.length"
            class="flex flex-wrap items-center justify-between px-4 mt-4 space-y-2 sm:px-0"
          >
            <div class="mr-4">
              <span
                v-for="(filter, index) in serverParams.filters"
                :key="index"
                class="mr-2 text-xs"
                :class="{'hidden': !excludedField(index) || !filter.length}"
              >
                <template
                  v-if="getMetaData(index) && getMetaData(index).label"
                >
                  <span class="font-medium">{{ getMetaData(index).label }}:</span>
                  <span class="space-x-1">
                    <span
                      v-for="(value, filter_index) in filter"
                      :key="filter_index"
                      class="inline-flex items-center py-0.5 pl-2 pr-2 rounded-full text-xxs font-medium bg-blue-100 text-blue-800"
                    >
                      {{ value }}
                      <button
                        type="button"
                        class="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none focus:bg-blue-500 focus:text-white"
                        @click="removeFilter(index, value)"
                      >
                        <span class="sr-only">Remove Filter</span>
                        <svg class="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
                        </svg>
                      </button>
                    </span>
                  </span>
                </template>
              </span>
            </div>
            <div class="flex justify-end flex-grow space-x-2">
              <button
                class="inline-flex items-center px-2 text-xs cursor-pointer tw-link hover:underline"
                @click="clearAllFilters"
                v-if="!isSavedSearch"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-0.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear filters
              </button>
              <button
                class="inline-flex items-center px-2 text-xs cursor-pointer tw-link hover:underline"
                @click="backToIndex"
                v-else
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-0.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              Back to {{ modelName }}s
              </button>
              <button
                class="py-0.5 text-xs tw-simple-button text-gray-700 rounded"
                @click="showSaveSearchModal"
                v-if="!isSavedSearch && modelUsesSavedSearches"
              >Save search</button>
              <button
                v-else-if="modelUsesSavedSearches"
                class="py-0.5 text-xs tw-simple-button text-gray-700 rounded"
                :class="{'hidden': !saveSearchSaveButton}"
                @click="showSaveSearchModal"
              >Update search</button>
            </div>
          </div>
          <div
            v-if="hasResults && includeMap"
            id="resultsMap"
            class="search-results-map"
          >
            <search-index-map
              :search-results="searchResults"
            />
          </div>
          <div>
            <slot name="results" :items="this.searchResults"/>
            <div v-if="this.total > 0 && !isSubscribed" class="my-4">
              <hidden-tab
                attribute-name="results"
                attribute-title="Results"
                :attribute-count="totalResultsLabel"
            >
            </hidden-tab>
            </div>
          </div>
          <nav
            v-if="hasResults"
            class="flex items-center justify-between px-4 py-3 border-t border-gray-200 sm:px-6"
            aria-label="Pagination"
          >
            <div class="hidden sm:block">
              <p class="text-sm text-gray-700">
                Showing
                <span class="font-medium">{{ this.getPaginationLowerBound() }}</span>
                to
                <span class="font-medium">{{ this.getPaginationUpperBound() }}</span>
                of
                <span class="font-medium">{{ totalResultsLabel }}</span>
                results
              </p>
            </div>
            <div v-if="isSubscribed" class="flex justify-between flex-1 sm:justify-end">
              <a v-if="!isFirstPage" @click="decrementPage" class="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                Previous
              </a>
              <a v-if="!isLastPage" @click="incrementPage" class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                Next
              </a>
            </div>
          </nav>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { getDirective } from 'vue-debounce'
import FacetFilter from './partials/FacetFilter.vue';
export default {
  components: { FacetFilter },

  directives: {
    debounce: getDirective()
  },

  props: {
    createRoute: {
      type: String,
      required: true,
    },
    canCreate: {
      type: Boolean,
      default: false,
    },
    pageTitle: {
      type: String,
      required: true,
    },
    subTitle: {
      type: String,
      required: false,
      default: null,
    },
    searchRoute: {
      type: String,
      required: true,
    },
    searchFilter: {
      type: [Array, Object],
      required: false,
    },
    placeHolder: {
      type: String,
      required: false,
      default: 'Search...',
    },
    includeMap: {
      type: Boolean,
      default: false,
    },
    excludedFields: {
      type: Array,
      required: false,
      default: () => {},
    },
    modelName: {
      type: String,
      required: false,
    },
    isSavedSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    savedSearchInfo: {
      type: [Array, Object],
      required: false,
    },
    hiddenSearch: {
      type: Boolean,
      required: false,
    },
    isSubscribed: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      searchResults: {},
      serverParams: {
        searchTerm: '',
        filters: {},
        pageSize: 10,
        page: 0,
      },
      total: 0,
      facetFilters: {},
      aggregations: {},
      hasResults: true,
      filterModel: window.location.pathname.substring(
        window.location.pathname.lastIndexOf ("/") + 1
      )+'_filters',
      errorMessages: {},
      saveSearchSaveButton: false,
      isLoading: true,
      hiddenFilter: this.searchFilter,
      mobileFiltersOpen: false,
    }
  },
  created() {
    this.sessionFilters();
    this.loadItems();
  },
  computed: {
    isFirstPage() {
      if (this.serverParams.page === 0) {
        return true;
      }
      return false;
    },
    isLastPage() {
      var numPages = Math.ceil(this.total/this.serverParams.pageSize);
      if (this.serverParams.page + 1 === numPages) {
        return true;
      }
      return false
    },
    totalResultsLabel() {
      if (this.total < 10000) {
        return this.total;
      }
      return 'total';
    },
    modelUsesSavedSearches() {
      return this.modelName !== 'project' && this.modelName !== 'newspost';
    }
  },
  methods: {
    onlyExcludedFields() {
      for (const filter in this.serverParams.filters) {
        if (this.excludedField(filter) && this.serverParams.filters[filter].length > 0) {
          return false;
        }
      }
      return true;
    },
    transformRefinementLabel(items) {
      return items.map(item => ({
        ...item,
        label: item.label.toLowerCase().titleCase()
      }));
    },
    updateSearch(val, e) {
      if (val === this.serverParams.searchTerm) {
        return;
      }
      this.serverParams.page = 0
      this.serverParams.searchTerm = val
      this.loadItems();
    },
    updatePageSize: function(e) {
      var val = e.target.value;
      this.serverParams.pageSize = val;
      this.loadItems();
    },
    setFilters(e) {
      this.serverParams.filters[e.name] = e.values;
      this.saveSearchSaveButton = true;
      this.loadItems();
    },
    async loadItems() {
      if (this.hiddenSearch) {
        this.isLoading = false;
        return true;
      }

      try {
        this.isLoading = true;
        if (this.hiddenFilter && Object.keys(this.serverParams.filters).length === 0) {
          this.serverParams.filters = this.hiddenFilter
        }
        sessionStorage.setItem(this.filterModel, JSON.stringify({'filters':this.serverParams.filters, 'page':this.serverParams.page, 'searchTerm':this.serverParams.searchTerm, 'pageSize':this.serverParams.pageSize}));

        const response = await axios.get( this.searchRoute,{ params: this.serverParams } );
        this.searchResults = response.data.results.filter(item => typeof item === 'object');
        this.total = response.data.totalRows;
        this.facetFilters = response.data.facetFilterList;
        this.aggregations = response.data.modelAggregations;
        this.isLoading = false;
      } catch (error) {
        this.rows = [];
        this.isLoading = false;
        return false;
      }
      return true;
    },
    showSaveSearchModal() {
      this.$refs.modal.show();
    },
    closeSaveSearchModal() {
      this.errorMessages = {};
      this.$refs.modal.hide();
    },
    storeSavedSearch() {
      const params = {
        filters: this.serverParams.filters,
        model: this.modelName,
        name: document.getElementById('save-search-name').value,
        description: document.getElementById('save-search-description').value
      };

      axios.post(this.route('saved_searches.store'), params)
        .then((response) => {
          this.closeSaveSearchModal();
        })
        .catch((error) => {
          var messages = [];
          for (const section in error.response.data.errors) {
            for (const message in  error.response.data.errors[section]) {
              messages.push(error.response.data.errors[section][message]);
            }
          }
          this.errorMessages = messages;
        });
    },

    updateSavedSearch() {
      const params = {
        filters: this.serverParams.filters,
        model: this.modelName,
        name: document.getElementById('save-search-name').value,
        description: document.getElementById('save-search-description').value
      };

      axios.patch(this.route('saved_searches.update', {'search': this.savedSearchInfo.id}), params)
        .then((response) => {
          this.closeSaveSearchModal();
        })
        .catch((error) => {
          var messages = [];
          for (const section in error.response.data.errors) {
            for (const message in  error.response.data.errors[section]) {
              messages.push(error.response.data.errors[section][message]);
            }
          }
          this.errorMessages = messages;
        });
    },
    toggleMapView() {
      document.getElementById("resultsMap").classList.toggle("active");
      document.getElementById("mapButton").classList.toggle("active");
      var text = document.getElementById("mapButtonText");
      if (text.innerHTML === "Show Map View") {
        text.innerHTML = "Hide Map View";
      } else {
        text.innerHTML = "Show Map View";
      }
    },
    getPaginationLowerBound() {
      return (this.serverParams.page*this.serverParams.pageSize) + 1;
    },
    getPaginationUpperBound() {
      var bound = (this.serverParams.page + 1)*this.serverParams.pageSize;
      if (bound <= this.total) {
        return bound;
      } else {
        return this.total;
      }
    },
    incrementPage() {
      this.serverParams.page = this.serverParams.page + 1;
      this.loadItems();
    },
    decrementPage() {
      this.serverParams.page = this.serverParams.page - 1;
      this.loadItems();
    },
    getMetaData( key ) {
      return this.aggregations[key];
    },
    excludedField(index) {
      return this.excludedFields && !this.excludedFields.includes(index);
    },
    searchClear(event) {
      if (document.getElementById('elastic-search-input').value === '') {
        this.serverParams.searchTerm = document.getElementById('elastic-search-input').value;
        this.loadItems();
      }
    },
    removeFilter(index, value) {
      var filter = this.serverParams.filters[this.getMetaData(index).field];
      filter.splice(filter.indexOf (value), 1);
      this.saveSearchSaveButton = true;
      this.loadItems();
    },
    clearAllFilters() {
      this.serverParams.filters = {};
      for (const child in this.$children) {
        if ('isSearchable' in this.$children[child]) {
          var filter = this.$children[child].$children[0].$children[0];
          filter.filters.values=[]
        }
      }
      for (const filter in this.hiddenFilter) {
        if (this.excludedField(filter)) {
          delete this.hiddenFilter[filter]
        }
      }
      this.loadItems();
    },
    sessionFilters() {
      var filters = sessionStorage.getItem(this.filterModel);
      if (filters && Object.keys(filters).length != 0) {
        var params = JSON.parse(sessionStorage.getItem(this.filterModel));
        this.serverParams.filters = params.filters;
        this.serverParams.page = params.page;
        this.serverParams.searchTerm = params.serachTerm;
        this.serverParams.pageSize = params.pageSize;
      } else {
        this.serverParams.filters = {};
      }
    },
    backToIndex() {
      switch (this.modelName) {
        case 'surgeon':
          window.location = this.route('surgeons.index');
          break;
        case 'distributor':
          window.location = this.route('distributors.index');
          break;
        case 'hospital':
          window.location = this.route('hospitals.index');
          break;
        case 'idn':
          window.location = this.route('idns.index');
          break;
        case 'gpo':
          window.location = this.route('gpos.index');
          break;
        case 'asc':
          window.location = this.route('ascs.index');
          break;
        case 'asccompanies':
          window.location = this.route('asccompanies.index');
          break;
        case 'manufacturer':
          window.location = this.route('manufacturers.index');
          break;
        case 'newspost':
          window.location = this.route('news.index');
          break;
      }
    },
    toggleMobileFilters() {
      this.mobileFiltersOpen = !this.mobileFiltersOpen;
    }
  }
};
</script>
